"use client";

import Link from "next/link";
import Image from "next/image";
import { getStrapiMedia } from "../utils/api-helpers";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { usePathname } from "next/navigation";
import JsonLd from "./JsonLd";

interface ServicesPageProps {
  data: {
    intro: {
      id: string
      preHeading: string
      heading: string
      lead: string
    }
    card: [
      {
        title: string
        text: string
        servicesLink: string
        image: {
          data: {
            attributes: {
              url: string
              name: string
              alternativeText: string
            }
          }
        }
      },
    ]
  }
}

export default function ServicesPage({ data }: ServicesPageProps) {
  const pathname = usePathname() ?? "";

  const isSingleService =
    pathname?.startsWith("/services/") && pathname !== "/services";

  const productSchemas = data.card.map((card) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": card.title,
    "description": card.text,
    "image": card.image?.data?.attributes?.url
      ? getStrapiMedia(card.image.data.attributes.url)
      : undefined,
    "brand": {
      "@type": "Brand",
      "name": "Coach America"
    }
  }));

  return (
    <>
      <JsonLd json={productSchemas} />
      <section className="py-6 dt:py-[10rem] px-1/2 dt:px-[9rem] text-center bg-white" id="ServicesPage">
        <div className="text-3/4 leading-3/4 dt:text-1.5 dt:leading-1.5 uppercase font-bold tracking-widest font-breit text-red mx-auto mb-1/2 dt:mb-1">
          {data.intro.preHeading}
        </div>
        <h1 className="tracking-wider font-bold uppercase text-2.25 leading-2.25 dt:text-[4.5rem] dt:leading-[4.5rem] mb-1.5 dt:mb-1 dt:w-2/3 mx-auto">
          {data.intro.heading}
        </h1>
        <Markdown
          remarkPlugins={[remarkGfm]}
          className="mb-3 dt:mb-4 text-center text-1 leading-1 dt:text-1.5 dt:leading-1.5 dt:w-2/3 dt:px-2 mx-auto"
        >
          {data.intro.lead}
        </Markdown>
        <div className={`${isSingleService ? 'flex flex-col items-center justify-center' : 'grid grid-cols-1 dt:grid-cols-2 gap-6'} px-4 dt:px-0 items-stretch`}>
          {data.card.map((card) => (
            <div className="flex flex-col text-center w-full mx-auto h-full justify-start gap-2" key={card.title}>
              {card.image?.data?.attributes?.url && (
                <Link href="/get-quote">
                  <Image
                    loading="lazy"
                    className="rounded-1/2 overflow-hidden"
                    src={getStrapiMedia(card.image.data.attributes.url) || "/placeholder.svg"}
                    width={568}
                    height={755}
                    alt={card.image.data.attributes.alternativeText || card.title}
                  />
                </Link>
              )}

              <div className="flex flex-col flex-grow justify-between h-full mt-[1.25rem] dt:mt-2">
                <h3 className="text-1.5 leading-1.5 dt:text-3 dt:leading-3 uppercase font-bold tracking-wider">
                  {card.title}
                </h3>
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  className="text-1 leading-1 dt:text-1.5 dt:leading-1.5 mt-1 dt:mt-1.5"
                >
                  {card.text}
                </Markdown>
                <Link
                  href={card.servicesLink && card.servicesLink.length > 0 ? card.servicesLink : "/get-quote"}
                  className={`bg-red hover:bg-blue text-white px-1.5 py-1 rounded-1/2 uppercase font-bold text-1 leading-1 tracking-wider mt-2 block mb-2 mx-auto`}
                >
                  {card.servicesLink && card.servicesLink.length > 0 ? "Learn more" : "Get quote"}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  )
}
